import { CapValueInput } from '../common/CapValueInput';
import {
  effectiveCapFor,
  formatCap,
  type AiCapTable,
  type AiRateLimitMode,
  type CapValue,
} from '../../services/aiRateLimitConfig';
import type { UserPlan } from '../../types';

/**
 * One user's daily AI cap: the tier default, or an explicit override.
 *
 * The override wins in BOTH directions — it can raise a cap, lower one, grant a
 * collector unlimited calls or pin a horologist to a number. That is what makes
 * it useful for the abuse case as well as the goodwill one, and it is why this
 * is not modelled as a "bonus".
 *
 * The effective-cap line is the point of the whole control. `capOverride` on its
 * own is unreadable — an empty box means "50, because Curator" to someone who
 * has the tier table memorised and nothing at all to everyone else — so the
 * number that will actually be enforced is always spelled out underneath.
 */
export function AiCapOverrideField({
  plan,
  caps,
  mode,
  usageCount,
  usageDay,
  value,
  onChange,
}: {
  plan: UserPlan;
  /** Live per-tier table, or null while it is still loading. */
  caps: AiCapTable | null;
  mode: AiRateLimitMode | null;
  usageCount?: number;
  usageDay?: string;
  /** `null` = no override stored; use the tier default. */
  value: CapValue | null;
  onChange: (next: CapValue | null) => void;
}) {
  const custom = value !== null;

  const effective = caps
    ? effectiveCapFor({
        plan,
        capOverride: custom ? value : undefined,
        caps,
      })
    : null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        AI Daily Cap
      </label>

      <div className="flex flex-wrap items-center gap-4 mb-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="aiCapMode"
            checked={!custom}
            onChange={() => onChange(null)}
            className="text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm text-gray-700">Use tier default</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="aiCapMode"
            checked={custom}
            onChange={() =>
              // Seed from the tier default rather than an empty box, so the
              // starting point is the number being overridden.
              onChange(caps ? caps[planTier(plan)] : 0)
            }
            className="text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm text-gray-700">Custom</span>
        </label>
      </div>

      {custom && (
        <div className="mb-2">
          <CapValueInput value={value} onChange={(next) => onChange(next)} />
        </div>
      )}

      <p className="text-xs text-gray-600">
        {effective === null ? (
          'Loading tier caps…'
        ) : (
          <>
            Effective cap:{' '}
            <span className="font-medium text-gray-900">
              {formatCap(effective)}
            </span>{' '}
            <span className="text-gray-500">
              ({custom ? 'override' : `${planLabel(plan)} tier`})
            </span>
          </>
        )}
      </p>

      {typeof usageCount === 'number' && (
        <p className="text-xs text-gray-500 mt-0.5">
          Used today: {usageCount}
          {usageDay ? ` · counter day ${usageDay} (UTC)` : ''}
        </p>
      )}

      {mode === 'credits' && (
        <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mt-2">
          The limiter is in <strong>credits</strong> mode, so no daily cap is
          being enforced right now. An override set here is stored and will
          apply the moment the mode is switched to daily.
        </p>
      )}
    </div>
  );
}

/** The tier a plan maps to. Anything unrecognised is free, as the backend does. */
function planTier(plan: UserPlan): keyof AiCapTable {
  return plan === 'collector' || plan === 'curator' || plan === 'horologist'
    ? plan
    : 'free';
}

function planLabel(plan: UserPlan): string {
  const tier = planTier(plan);
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
